import { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './components/Sidebar';
import DashboardSlider from './components/DashboardSlider';
import DashboardV2 from './components/DashboardV2';
import Settings from './components/Settings';
import './App.css';
import axios from 'axios';
import DownloadPageV2 from './components/DownloadPageV2';
import AlertPage from './components/AlertPage';
import { Box, CircularProgress } from '@mui/material';
import HeaderPage from './components/HeaderPage';
import MobileNavMenu from './components/MobileNavMenu';
import AuthPage from './components/AuthPage';
import { useAuth } from './contexts/AuthContext';
import AdminPage from './components/AdminPage';
import StationDetailsModal from './components/StationDetailsModal';
import StationNotesModal from './components/StationNotesModal';

function App() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [temperatureData, setTemperatureData] = useState([]);
  const [humidityData, setHumidityData] = useState([]);
  const [CO2Data, setCO2Data] = useState([]);
  const [TVOCData, setTVOCData] = useState([]);
  const [soundData, setSoundData] = useState([]);
  const [PM1_0Data, setPM1_0Data] = useState([]);
  const [PM2_5Data, setPM2_5Data] = useState([]);
  const [PM10Data, setPM10Data] = useState([]);
  const [groundHumidityData, setGroundHumidityData] = useState([]);

  const [currentMenu, setCurrentMenu] = useState(localStorage.getItem('currentMenu') || 'dashboard');
  const [notifications, setNotifications] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [alertSent, setAlertSent] = useState({ temperature: false, CO2: false, TVOC: false, sound: false, PM1_0: false, PM2_5: false, PM10: false, groundHumidity: false, humidity: false });
  const [alertActivate, setAlertActivate] = useState({ temperature: true, CO2: true, TVOC: true, sound: true, PM1_0: true, PM2_5: true, PM10: true, groundHumidity: true, humidity: true });
  const [alertValue, setAlertValue] = useState({ temperature: [19, 35], CO2: [0, 1000], TVOC: [0, 500], sound: [0, 80], PM1_0: [0, 15], PM2_5: [0, 15], PM10: [0, 45], groundHumidity: [40, 80], humidity: [30, 65] });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [timeRange, setTimeRange] = useState('10min');
  const [selectedDevice, setSelectedDevice] = useState('outdoor1');
  const [selectedStationForDetails, setSelectedStationForDetails] = useState(null);
  const [stationProfiles, setStationProfiles] = useState({});
  const [stationProfileLoading, setStationProfileLoading] = useState(false);
  const [stationProfileError, setStationProfileError] = useState('');
  const [stationThresholds, setStationThresholds] = useState({});
  const [alertPagePreset, setAlertPagePreset] = useState({
    tableName: '',
    deviceId: '',
    requestKey: 0,
  });
  const [selectedStationForNotes, setSelectedStationForNotes] = useState(null);
  const [stationNotes, setStationNotes] = useState({});
  const [stationNotesLoading, setStationNotesLoading] = useState(false);
  const [stationNotesError, setStationNotesError] = useState('');


  const [realTimeData, setRealTimeData] = useState({});
  const [realTimeDataByTable, setRealTimeDataByTable] = useState({
    climatrack: {},
    aquacheck: {},
    greenwatch: {},
  });

  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [hasDashboardLoadedOnce, setHasDashboardLoadedOnce] = useState(false);

  const isFetchingLatestDataRef = useRef(false);


  const [selectedTable, setSelectedTable] = useState('climatrack');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollToFavorites, setScrollToFavorites] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";
  const DASHBOARD_AUTO_REFRESH_MS = 10 * 60 * 1000;


  const stationKeyOf = (tableName, deviceId) =>
    `${String(tableName || '').toLowerCase()}:${String(deviceId || '')}`;

  const selectedStationDetailsTable =
    selectedStationForDetails?.table || selectedTable;

  const selectedStationDetailsId =
    selectedStationForDetails?.id || selectedDevice;

  const selectedStationNotesTable =
    selectedStationForNotes?.table || selectedTable;

  const selectedStationNotesId =
    selectedStationForNotes?.id || selectedDevice;

  const selectedStationNotesKey =
    selectedStationNotesTable && selectedStationNotesId
      ? stationKeyOf(selectedStationNotesTable, selectedStationNotesId)
      : '';

  const selectedStationNotesProfile =
    selectedStationNotesKey
      ? stationProfiles[selectedStationNotesKey]
      : undefined;

  const selectedStationNotesList =
    selectedStationNotesKey
      ? stationNotes[selectedStationNotesKey] || []
      : [];

  const selectedStationKey =
    selectedStationDetailsTable && selectedStationDetailsId
      ? stationKeyOf(selectedStationDetailsTable, selectedStationDetailsId)
      : '';

  const selectedStationProfile = selectedStationKey
    ? stationProfiles[selectedStationKey]
    : undefined;

  const normalizeThresholds = (thresholdsFromApi = {}) => {
    const normalized = {};

    Object.entries(thresholdsFromApi || {}).forEach(([metric, limits]) => {
      normalized[metric] = {
        min:
          limits?.min === null || limits?.min === undefined
            ? null
            : Number(limits.min),
        max:
          limits?.max === null || limits?.max === undefined
            ? null
            : Number(limits.max),
      };
    });

    return normalized;
  };

  const fetchStationProfile = useCallback(
    async (tableName, deviceId, options = {}) => {
      if (!isAuthenticated || !tableName || !deviceId) {
        return undefined;
      }

      const token = localStorage.getItem('token');
      const key = stationKeyOf(tableName, deviceId);

      if (!token) {
        if (options.withLoading) {
          setStationProfileError(
            'Aucun jeton d’authentification disponible.'
          );
        }
        return undefined;
      }

      if (options.withLoading) {
        setStationProfileLoading(true);
        setStationProfileError('');
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/stations/${encodeURIComponent(
            tableName
          )}/${encodeURIComponent(deviceId)}/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setStationProfiles((previousProfiles) => ({
          ...previousProfiles,
          [key]: response.data,
        }));

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Impossible de charger la fiche de la station.';

        console.error(
          'Erreur lors du chargement de la fiche station :',
          error
        );

        if (options.withLoading) {
          setStationProfileError(errorMessage);
        }

        return undefined;
      } finally {
        if (options.withLoading) {
          setStationProfileLoading(false);
        }
      }
    },
    [API_URL, isAuthenticated]
  );

  const saveStationProfile = useCallback(
    async (tableName, deviceId, updatedProfile) => {
      if (!tableName || !deviceId) {
        throw new Error('Aucune station sélectionnée.');
      }

      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error(
          'Aucun jeton d’authentification disponible.'
        );
      }

      const response = await axios.put(
        `${API_URL}/api/stations/${encodeURIComponent(
          tableName
        )}/${encodeURIComponent(deviceId)}/profile`,
        {
          name: updatedProfile.name || '',
          isFavorite: updatedProfile.isFavorite === true,
          description: updatedProfile.description || '',
          photoUrl: updatedProfile.photoUrl || '',

          locationName: updatedProfile.locationName || '',
          address: updatedProfile.address || '',
          latitude:
            updatedProfile.latitude === null ||
              updatedProfile.latitude === undefined
              ? ''
              : updatedProfile.latitude,
          longitude:
            updatedProfile.longitude === null ||
              updatedProfile.longitude === undefined
              ? ''
              : updatedProfile.longitude,

          environmentName: updatedProfile.environmentName || '',
          environmentData:
            updatedProfile.environmentData &&
              typeof updatedProfile.environmentData === 'object'
              ? updatedProfile.environmentData
              : {},
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const savedProfile = response.data.profile || response.data;
      const key = stationKeyOf(tableName, deviceId);

      setStationProfiles((previousProfiles) => ({
        ...previousProfiles,
        [key]: savedProfile,
      }));

      return savedProfile;
    },
    [API_URL]
  );

  const handleStationNotesProfileChange = useCallback(
    async (updatedProfilePart) => {
      if (!selectedStationNotesTable || !selectedStationNotesId) {
        throw new Error('Aucune station sélectionnée.');
      }

      const tableName = String(selectedStationNotesTable).toLowerCase();
      const deviceId = String(selectedStationNotesId);
      const key = stationKeyOf(tableName, deviceId);

      let currentProfile =
        stationProfiles[key] ||
        selectedStationNotesProfile ||
        {};

      /*
       * Sécurité :
       * si la fiche n'est pas encore chargée au moment du clic sur Enregistrer,
       * on tente de la récupérer pour ne pas écraser le nom, favori, localisation, etc.
       */
      if (Object.keys(currentProfile).length === 0) {
        const loadedProfile = await fetchStationProfile(tableName, deviceId, {
          withLoading: false,
        });

        currentProfile = loadedProfile || {};
      }

      return await saveStationProfile(tableName, deviceId, {
        ...currentProfile,
        description: updatedProfilePart.description || '',
        photoUrl: updatedProfilePart.photoUrl || '',
      });
    },
    [
      saveStationProfile,
      fetchStationProfile,
      stationProfiles,
      selectedStationNotesTable,
      selectedStationNotesId,
      selectedStationNotesProfile,
    ]
  );

  const fetchStationNotes = useCallback(
    async (tableName, deviceId) => {
      if (!isAuthenticated || !tableName || !deviceId) {
        return [];
      }

      const token = localStorage.getItem('token');

      if (!token) {
        setStationNotesError(
          'Aucun jeton d’authentification disponible.'
        );
        return [];
      }

      const normalizedTableName = String(tableName).toLowerCase();
      const normalizedDeviceId = String(deviceId);
      const key = stationKeyOf(normalizedTableName, normalizedDeviceId);

      setStationNotesLoading(true);
      setStationNotesError('');

      try {
        const response = await axios.get(
          `${API_URL}/api/stations/${encodeURIComponent(
            normalizedTableName
          )}/${encodeURIComponent(normalizedDeviceId)}/notes`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const notes = Array.isArray(response.data)
          ? response.data
          : [];

        setStationNotes((previousNotes) => ({
          ...previousNotes,
          [key]: notes,
        }));

        return notes;
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Impossible de charger les notes de la station.';

        console.error(
          'Erreur lors du chargement des notes :',
          error
        );

        setStationNotesError(errorMessage);
        return [];
      } finally {
        setStationNotesLoading(false);
      }
    },
    [API_URL, isAuthenticated]
  );

  const createStationNote = useCallback(
    async ({ content, isPrivate }) => {
      if (!selectedStationNotesTable || !selectedStationNotesId) {
        throw new Error('Aucune station sélectionnée.');
      }

      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error(
          'Aucun jeton d’authentification disponible.'
        );
      }

      const tableName = String(selectedStationNotesTable).toLowerCase();
      const deviceId = String(selectedStationNotesId);
      const key = stationKeyOf(tableName, deviceId);

      try {
        const response = await axios.post(
          `${API_URL}/api/stations/${encodeURIComponent(
            tableName
          )}/${encodeURIComponent(deviceId)}/notes`,
          {
            content,
            isPrivate: isPrivate === true,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const createdNote = response.data;

        setStationNotes((previousNotes) => ({
          ...previousNotes,
          [key]: [
            createdNote,
            ...(previousNotes[key] || []),
          ],
        }));

        return createdNote;
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Impossible de créer la note.';

        console.error(
          'Erreur lors de la création de la note :',
          error
        );

        throw new Error(errorMessage);
      }
    },
    [
      API_URL,
      selectedStationNotesTable,
      selectedStationNotesId,
    ]
  );

  const deleteStationNote = useCallback(
    async (noteId) => {
      if (!selectedStationNotesTable || !selectedStationNotesId) {
        throw new Error('Aucune station sélectionnée.');
      }

      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error(
          'Aucun jeton d’authentification disponible.'
        );
      }

      const tableName = String(selectedStationNotesTable).toLowerCase();
      const deviceId = String(selectedStationNotesId);
      const key = stationKeyOf(tableName, deviceId);

      try {
        await axios.delete(
          `${API_URL}/api/stations/${encodeURIComponent(
            tableName
          )}/${encodeURIComponent(deviceId)}/notes/${encodeURIComponent(
            noteId
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setStationNotes((previousNotes) => ({
          ...previousNotes,
          [key]: (previousNotes[key] || []).filter(
            (note) => note.id !== noteId
          ),
        }));
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          'Impossible de supprimer la note.';

        console.error(
          'Erreur lors de la suppression de la note :',
          error
        );

        throw new Error(errorMessage);
      }
    },
    [
      API_URL,
      selectedStationNotesTable,
      selectedStationNotesId,
    ]
  );

  const handleStationProfileChange = async (updatedProfile) => {
    try {
      setStationProfileError('');

      return await saveStationProfile(
        selectedStationDetailsTable,
        selectedStationDetailsId,
        updatedProfile
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Impossible d’enregistrer la fiche de la station.';

      console.error(
        'Erreur lors de l’enregistrement de la fiche station :',
        error
      );

      setStationProfileError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleOpenStationDetails = useCallback(
    (station) => {
      const tableName = String(station?.table || '').toLowerCase();
      const deviceId = String(station?.id || '');

      if (!tableName || !deviceId) {
        return;
      }

      setSelectedTable(tableName);
      setSelectedDevice(deviceId);
      setSelectedStationForDetails({
        table: tableName,
        id: deviceId,
      });

      fetchStationProfile(tableName, deviceId, {
        withLoading: true,
      });
    },
    [fetchStationProfile]
  );

  const handleOpenStationNotes = useCallback(
    (station) => {
      const tableName = String(
        station?.table || station?.stationType || ''
      ).toLowerCase();

      const deviceId = String(
        station?.id || station?.stationId || ''
      );

      if (!tableName || !deviceId) {
        return;
      }

      setSelectedTable(tableName);
      setSelectedDevice(deviceId);

      setSelectedStationForNotes({
        table: tableName,
        id: deviceId,
      });

      /*
       * On charge aussi la fiche pour récupérer description + image.
       */
      fetchStationProfile(tableName, deviceId, {
        withLoading: false,
      });

      fetchStationNotes(tableName, deviceId);
    },
    [
      fetchStationProfile,
      fetchStationNotes,
    ]
  );

  const handleCloseStationNotes = () => {
    setSelectedStationForNotes(null);
    setStationNotesError('');
  };

  const handleCloseStationDetails = () => {
    setSelectedStationForDetails(null);
    setStationProfileError('');
  };

  const handleToggleDashboardFavorite = useCallback(
    async (station) => {
      const tableName = String(station?.table || '').toLowerCase();
      const deviceId = String(station?.id || '');

      if (!tableName || !deviceId) {
        return;
      }

      const key = stationKeyOf(tableName, deviceId);
      const currentProfile = stationProfiles[key] || {};
      const updatedProfile = {
        ...currentProfile,
        isFavorite: !currentProfile.isFavorite,
      };

      setStationProfiles((previousProfiles) => ({
        ...previousProfiles,
        [key]: updatedProfile,
      }));

      try {
        await saveStationProfile(
          tableName,
          deviceId,
          updatedProfile
        );
      } catch (error) {
        console.error(
          'Erreur lors de la modification du favori :',
          error
        );

        setStationProfiles((previousProfiles) => ({
          ...previousProfiles,
          [key]: currentProfile,
        }));
      }
    },
    [saveStationProfile, stationProfiles]
  );

  const handleOpenStationAlertSettings = ({
    stationType,
    stationId,
  }) => {
    const normalizedTableName = String(
      stationType || ''
    ).toLowerCase();

    const normalizedDeviceId = String(stationId || '');

    if (!normalizedTableName || !normalizedDeviceId) {
      console.error(
        'Impossible d’ouvrir les alertes : station invalide.'
      );
      return;
    }

    setAlertPagePreset((previousPreset) => ({
      tableName: normalizedTableName,
      deviceId: normalizedDeviceId,
      requestKey: previousPreset.requestKey + 1,
    }));

    setCurrentMenu('alerts');
    setMobileMenuOpen(false);
    setSelectedStationForDetails(null);

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  };

  useEffect(() => {
    if (!selectedStationForDetails) {
      return;
    }

    fetchStationProfile(
      selectedStationDetailsTable,
      selectedStationDetailsId,
      {
        withLoading: true,
      }
    );
  }, [
    selectedStationForDetails,
    selectedStationDetailsTable,
    selectedStationDetailsId,
    fetchStationProfile,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const token = localStorage.getItem('token');

    if (!token) {
      return;
    }

    const entries = Object.entries(realTimeDataByTable).flatMap(
      ([tableName, devices]) =>
        Object.keys(devices || {}).map((deviceId) => ({
          tableName,
          deviceId,
          key: stationKeyOf(tableName, deviceId),
        }))
    );

    const missingEntries = entries.filter(
      ({ key }) => !stationProfiles[key]
    );

    if (missingEntries.length === 0) {
      return;
    }

    let cancelled = false;

    Promise.all(
      missingEntries.map(async ({ tableName, deviceId, key }) => {
        try {
          const response = await axios.get(
            `${API_URL}/api/stations/${encodeURIComponent(
              tableName
            )}/${encodeURIComponent(deviceId)}/profile`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          return [key, response.data];
        } catch (error) {
          console.error(
            `Erreur lors du chargement de la fiche ${key} :`,
            error
          );
          return null;
        }
      })
    ).then((results) => {
      if (cancelled) {
        return;
      }

      const loadedProfiles = {};

      results.forEach((result) => {
        if (result) {
          loadedProfiles[result[0]] = result[1];
        }
      });

      if (Object.keys(loadedProfiles).length > 0) {
        setStationProfiles((previousProfiles) => ({
          ...previousProfiles,
          ...loadedProfiles,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    API_URL,
    isAuthenticated,
    realTimeDataByTable,
    stationProfiles,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const token = localStorage.getItem('token');

    if (!token) {
      return;
    }

    const entries = Object.entries(realTimeDataByTable).flatMap(
      ([tableName, devices]) =>
        Object.keys(devices || {}).map((deviceId) => ({
          tableName,
          deviceId,
          key: stationKeyOf(tableName, deviceId),
        }))
    );

    if (entries.length === 0) {
      return;
    }

    let cancelled = false;

    Promise.all(
      entries.map(async ({ tableName, deviceId, key }) => {
        try {
          const response = await axios.get(
            `${API_URL}/api/alerts/thresholds`,
            {
              params: {
                tableName,
                deviceId,
              },
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          return [key, normalizeThresholds(response.data)];
        } catch (error) {
          console.error(
            `Erreur lors du chargement des seuils ${key} :`,
            error
          );
          return null;
        }
      })
    ).then((results) => {
      if (cancelled) {
        return;
      }

      const loadedThresholds = {};

      results.forEach((result) => {
        if (result) {
          loadedThresholds[result[0]] = result[1];
        }
      });

      setStationThresholds(loadedThresholds);
    });

    return () => {
      cancelled = true;
    };
  }, [
    API_URL,
    isAuthenticated,
    realTimeDataByTable,
    currentMenu,
  ]);

  // Ajout d'une notification
  const addNotification = (message) => {
    const timestamp = new Date().toLocaleString();
    const rawTimestamp = new Date();
    setNotifications((prev) => [...prev, { ...message, timestamp, rawTimestamp, read: false }]);
    setAlertCount((prev) => prev + 1);
  };

  // Vérification des seuils
  const checkThresholds = useCallback(
    (data, sensorId, sensorName) => {
      if (data.temperature > alertValue.temperature[1] && !alertSent.temperature && alertActivate.temperature) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Température élevée',
          message: 'La température a dépassé le seuil.',
          value: `${data.temperature}°C`,
          recommendation: 'Vérifiez la climatisation.',
        });
        setAlertSent((prev) => ({ ...prev, temperature: true }));
      }
      if (data.temperature < alertValue.temperature[0] && !alertSent.temperature && alertActivate.temperature) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Température faible',
          message: 'La température est inférieure au seuil.',
          value: `${data.temperature}°C`,
          recommendation: 'Vérifiez la climatisation.',
        });
        setAlertSent((prev) => ({ ...prev, temperature: true }));
      }
      if (data.CO2 > alertValue.CO2[1] && !alertSent.CO2 && alertActivate.CO2) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de CO2 élevé',
          message: 'Le CO2 a dépassé le seuil.',
          value: `${data.CO2} ppm`,
          recommendation: 'Aérez la pièce.',
        });
        setAlertSent((prev) => ({ ...prev, CO2: true }));
      }
      if (data.CO2 < alertValue.CO2[0] && !alertSent.CO2 && alertActivate.CO2) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de CO2 faible',
          message: 'Le CO2 est inférieur au seuil.',
          value: `${data.CO2} ppm`,
          recommendation: 'Aérez la pièce.',
        });
        setAlertSent((prev) => ({ ...prev, CO2: true }));
      }
      if (data.TVOC > alertValue.TVOC[1] && !alertSent.TVOC && alertActivate.TVOC) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de TVOC élevé',
          message: 'Le TVOC a dépassé le seuil.',
          value: `${data.TVOC}`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, TVOC: true }));
      }
      if (data.TVOC < alertValue.TVOC[0] && !alertSent.TVOC && alertActivate.TVOC) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de TVOC faible',
          message: 'Le TVOC est inférieur au seuil.',
          value: `${data.TVOC}`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, TVOC: true }));
      }
      if (data.sound_level > alertValue.sound[1] && !alertSent.sound && alertActivate.sound) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau sonore élevé',
          message: 'Le bruit a dépassé le seuil.',
          value: `${data.sound_level} dB`,
          recommendation: 'Réduisez le bruit.',
        });
        setAlertSent((prev) => ({ ...prev, sound: true }));
      }
      if (data.sound_level < alertValue.sound[0] && !alertSent.sound && alertActivate.sound) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau sonore faible',
          message: 'Le bruit est inférieur au seuil.',
          value: `${data.sound_level} dB`,
          recommendation: 'Augmenter le son',
        });
        setAlertSent((prev) => ({ ...prev, sound: true }));
      }
      if (data.PM1_0 > alertValue.PM1_0[1] && !alertSent.PM1_0 && alertActivate.PM1_0) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de PM1.0 élevé',
          message: 'Le PM1.0 a dépassé le seuil.',
          value: `${data.PM1_0} µg/m³`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, PM1_0: true }));
      }
      if (data.PM1_0 < alertValue.PM1_0[0] && !alertSent.PM1_0 && alertActivate.PM1_0) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de PM1.0 faible',
          message: 'Le PM1.0 est inférieur au seuil.',
          value: `${data.PM1_0} µg/m³`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, PM1_0: true }));
      }
      if (data.PM2_5 > alertValue.PM2_5[1] && !alertSent.PM2_5 && alertActivate.PM2_5) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de PM2.5 élevé',
          message: 'Le PM2.5 a dépassé le seuil.',
          value: `${data.PM2_5} µg/m³`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, PM2_5: true }));
      }
      if (data.PM2_5 < alertValue.PM2_5[0] && !alertSent.PM2_5 && alertActivate.PM2_5) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de PM2.5 faible',
          message: 'Le PM2.5 est inférieur au seuil.',
          value: `${data.PM2_5} µg/m³`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, PM2_5: true }));
      }
      if (data.PM10 > alertValue.PM10[1] && !alertSent.PM10 && alertActivate.PM10) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de PM10 élevé',
          message: 'Le PM10 a dépassé le seuil.',
          value: `${data.PM10} µg/m³`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, PM10: true }));
      }
      if (data.PM10 < alertValue.PM10[0] && !alertSent.PM10 && alertActivate.PM10) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Niveau de PM10 faible',
          message: 'Le PM10 est inférieur au seuil.',
          value: `${data.PM10} µg/m³`,
          recommendation: 'Vérifiez les sources de pollution.',
        });
        setAlertSent((prev) => ({ ...prev, PM10: true }));
      }
      if (data.ground_humidity > alertValue.groundHumidity[1] && !alertSent.groundHumidity && alertActivate.groundHumidity) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Humidité du sol élevée',
          message: 'L humidité du sol est supérieure au seuil.',
          value: `${data.ground_humidity}%`,
          recommendation: 'Réduisez l arrosage.',
        });
        setAlertSent((prev) => ({ ...prev, groundHumidity: true }));
      }
      if (data.ground_humidity < alertValue.groundHumidity[0] && !alertSent.groundHumidity && alertActivate.groundHumidity) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Humidité du sol faible',
          message: 'L humidité du sol est inférieure au seuil.',
          value: `${data.ground_humidity}%`,
          recommendation: 'Arrosez le sol.',
        });
        setAlertSent((prev) => ({ ...prev, groundHumidity: true }));
      }
      if (data.humidity > alertValue.humidity[1] && !alertSent.humidity && alertActivate.humidity) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Humidité élevée',
          message: 'L humidité a dépassé le seuil.',
          value: `${data.humidity}%`,
          recommendation: 'Vérifiez la ventilation.',
        });
        setAlertSent((prev) => ({ ...prev, humidity: true }));
      }
      if (data.humidity < alertValue.humidity[0] && !alertSent.humidity && alertActivate.humidity) {
        addNotification({
          sensorId,
          sensorName,
          title: 'Humidité faible',
          message: 'L humidité est inférieure au seuil.',
          value: `${data.humidity}%`,
          recommendation: 'Vérifiez la ventilation.',
        });
        setAlertSent((prev) => ({ ...prev, humidity: true }));
      }
    },
    [alertSent, alertValue, alertActivate]
  );

  // 1) Calcul automatique de startDate/endDate en fonction de timeRange
  useEffect(() => {
    const now = new Date();
    let newStart = null;
    let newEnd = null;

    if (timeRange === '10min') {
      newStart = new Date(now - 10 * 60 * 1000); // 10 dernières minutes
      newEnd = new Date(now.getTime() + 60 * 60 * 1000); // Jusqu'à l'heure actuelle
    } else if (timeRange === 'yesterday') {
      newStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0); // Minuit d'hier
      newEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0); // Minuit d'aujourd'hui
    } else if (timeRange === 'today') {
      newStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0); // Minuit aujourd'hui
      newEnd = new Date(now.getTime() + 60 * 60 * 1000);; // Jusqu'à l'heure actuelle
    } else if (timeRange === '1m') {
      newStart = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0); // Minuit aujourd'hui
      newEnd = new Date(now.getTime() + 60 * 60 * 1000);; // Jusqu'à l'heure actuelle
    }

    setStartDate(newStart);
    setEndDate(newEnd);
  }, [timeRange]);

  // -----------------------------------------------------------
  // useEffect pour récupérer les données (utilisées par l'export)
  // en fonction du "selectedDevice" / "selectedTable" / plage de temps
  // -----------------------------------------------------------

  useEffect(() => {
    // Vider les tableaux pour éviter l'accumulation
    setTemperatureData([]);
    setHumidityData([]);
    setCO2Data([]);
    setTVOCData([]);
    setSoundData([]);
    setPM1_0Data([]);
    setPM2_5Data([]);
    setPM10Data([]);
    setGroundHumidityData([]);

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${API_URL}/api/data/${selectedTable}/${selectedDevice}/filter`,
          {
            params: {
              startTime: startDate.toISOString(),
              endTime: endDate.toISOString(),
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log(`Données récupérées pour ${selectedDevice} entre ${startDate.toISOString()} et ${endDate.toISOString()}:`, response.data);
        const rows = response.data;

        rows.forEach((row) => {
          const timestamp = new Date(row.timestamp).getTime(); // Conversion du timestamp
          if (row.temperature) {
            setTemperatureData((prev) => [...prev, [timestamp, parseFloat(row.temperature)]]);
          }
          if (row.humidity) {
            setHumidityData((prev) => [...prev, [timestamp, parseFloat(row.humidity)]]);
          }
          if (row.co2) {
            setCO2Data((prev) => [...prev, [timestamp, parseFloat(row.co2)]]);
          }
          if (row.tvoc) {
            setTVOCData((prev) => [...prev, [timestamp, parseFloat(row.tvoc)]]);
          }
          if (row.sound_level) {
            setSoundData((prev) => [...prev, [timestamp, parseFloat(row.sound_level)]]);
          }
          if (row.pm1_0) {
            setPM1_0Data((prev) => [...prev, [timestamp, parseFloat(row.pm1_0)]]);
          }
          if (row.pm2_5) {
            setPM2_5Data((prev) => [...prev, [timestamp, parseFloat(row.pm2_5)]]);
          }
          if (row.pm10) {
            setPM10Data((prev) => [...prev, [timestamp, parseFloat(row.pm10)]]);
          }
          if (row.ground_humidity) {
            setGroundHumidityData((prev) => [...prev, [timestamp, parseFloat(row.ground_humidity)]]);
          }
          checkThresholds({
            temperature: parseFloat(row.temperature),
            CO2: parseFloat(row.co2),
            TVOC: parseFloat(row.tvoc),
            sound_level: parseFloat(row.sound_level),
            PM1_0: parseFloat(row.pm1_0),
            PM2_5: parseFloat(row.pm2_5),
            PM10: parseFloat(row.pm10),
            ground_humidity: parseFloat(row.ground_humidity),
            humidity: parseFloat(row.humidity)
          }, selectedDevice, selectedTable);
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      }
    };

    if (selectedDevice && startDate && endDate) {
      fetchData();
    }
  }, [selectedDevice, startDate, endDate, checkThresholds, selectedTable, API_URL]);

  // Sauvegarde du menu dans localStorage
  useEffect(() => {
    localStorage.setItem('currentMenu', currentMenu);
  }, [currentMenu]);

  // Gestion du menu
  const handleMenuClick = (menu) => {
    setCurrentMenu(menu);
    setMobileMenuOpen(false);
  };

  const openMobileMenu = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileNavClick = (menu) => {
    if (menu === 'favorites') {
      setCurrentMenu('dashboard');
      setScrollToFavorites(true);
    } else {
      setCurrentMenu(menu);
    }
    setMobileMenuOpen(false);
  };

  // Nettoyer les notifications
  const clearNotifications = () => {
    setNotifications([]);
    setAlertCount(0);
  };

  // Marquer une alerte comme lue
  const handleAlertClick = (index) => {
    const updatedNotifications = notifications.map((notification, i) => {
      if (i === index) {
        return { ...notification, read: true };
      }
      return notification;
    });

    setNotifications(updatedNotifications);
    setAlertCount(updatedNotifications.filter((notification) => !notification.read).length);
  };

  const fetchLatestData = useCallback(
    async ({ initialLoad = false } = {}) => {
      const token = localStorage.getItem('token');

      if (!isAuthenticated) {
        setIsDashboardLoading(false);
        return;
      }

      if (!token) {
        console.warn('Token indisponible au moment du chargement du dashboard.');

        /*
         * Important :
         * on évite de laisser le dashboard bloqué indéfiniment
         * sur "Chargement des boîtiers...".
         */
        setIsDashboardLoading(false);
        setHasDashboardLoadedOnce(true);
        return;
      }

      if (isFetchingLatestDataRef.current) {
        return;
      }

      isFetchingLatestDataRef.current = true;

      if (initialLoad) {
        setIsDashboardLoading(true);
      }

      try {
        const tables = ['climatrack', 'aquacheck', 'greenwatch'];

        const byTable = {
          climatrack: {},
          aquacheck: {},
          greenwatch: {},
        };

        await Promise.all(
          tables.map(async (table) => {
            try {
              const devicesResponse = await axios.get(
                `${API_URL}/api/devices`,
                {
                  params: {
                    table,
                    _: Date.now(),
                  },
                  headers: {
                    Authorization: `Bearer ${token}`,

                  },
                }
              );

              const deviceIDs = Array.isArray(devicesResponse.data)
                ? devicesResponse.data.map((id) => String(id))
                : [];

              const latestData = {};

              await Promise.all(
                deviceIDs.map(async (deviceId) => {
                  try {
                    const latestResponse = await axios.get(
                      `${API_URL}/api/data/${table}/${encodeURIComponent(
                        deviceId
                      )}/latest`,
                      {
                        params: {
                          _: Date.now(),
                        },
                        headers: {
                          Authorization: `Bearer ${token}`,

                        },
                      }
                    );

                    latestData[deviceId] = latestResponse.data || {};
                  } catch (error) {
                    console.error(
                      `Erreur latest pour ${table}/${deviceId} :`,
                      error
                    );

                    /*
                     * Le boîtier reste affiché même si sa dernière
                     * valeur n'est pas récupérable.
                     */
                    latestData[deviceId] = {};
                  }
                })
              );

              byTable[table] = latestData;
            } catch (error) {
              console.error(
                `Erreur lors du chargement des boîtiers ${table} :`,
                error
              );

              byTable[table] = {};
            }
          })
        );

        console.log('Données dashboard récupérées :', byTable);

        setRealTimeDataByTable(byTable);
        setRealTimeData(byTable[selectedTable] || {});
      } catch (error) {
        console.error(
          'Erreur générale lors du chargement du dashboard :',
          error
        );
      } finally {
        setHasDashboardLoadedOnce(true);
        setIsDashboardLoading(false);
        isFetchingLatestDataRef.current = false;
      }
    },
    [
      API_URL,
      isAuthenticated,
      selectedTable,
    ]
  );

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!isAuthenticated) {
      setIsDashboardLoading(false);
      setHasDashboardLoadedOnce(true);
      return undefined;
    }

    /*
     * Chargement immédiat au démarrage ou après refresh manuel.
     */
    fetchLatestData({
      initialLoad: true,
    });

    /*
     * Mise à jour automatique toutes les 10 minutes,
     * sans refresh complet de la page.
     */
    const interval = setInterval(() => {
      fetchLatestData();
    }, DASHBOARD_AUTO_REFRESH_MS);

    return () => {
      clearInterval(interval);
    };
  }, [
    authLoading,
    isAuthenticated,
    fetchLatestData,
  ]);
  useEffect(() => {
    setRealTimeData(realTimeDataByTable[selectedTable] || {});
  }, [selectedTable, realTimeDataByTable]);

  const toggleSidebarCollapsed = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const toggleSlider = () => {
    setIsSliderOpen(!isSliderOpen);
  };





  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Afficher un loader pendant la vérification de l'authentification
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#141526'
        }}
      >
        <CircularProgress sx={{ color: '#FF9800' }} size={60} />
      </Box>
    );
  }

  // Afficher la page d'authentification si non connecté
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return (
    <Box sx={{ display: 'flex', backgroundColor: '#141526', overflowX: 'hidden' }}>
      {/* Afficher le bouton burger uniquement sur mobile */}
      {isMobile && currentMenu !== 'dashboard' && currentMenu !== 'download' && (
        <HeaderPage
          onOpenMenu={openMobileMenu}
          toggleSlider={toggleSlider}
        />
      )}

      <MobileNavMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        currentMenu={currentMenu}
        onMenuClick={handleMobileNavClick}
        alertCount={alertCount}
      />

      {/* Sidebar desktop uniquement — le mobile utilise MobileNavMenu */}
      {!isMobile && (
        <Sidebar
          onMenuClick={handleMenuClick}
          currentMenu={currentMenu}
          alertCount={alertCount}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
        />
      )}

      {(isMobile && isSliderOpen) && (
        <DashboardSlider
          open={isSliderOpen}
          onClose={toggleSlider}
          realTimeData={realTimeData}
          selectedDevice={selectedDevice}
          selectedTable={selectedTable}
        />

      )}

      <StationDetailsModal
        stationId={selectedStationDetailsId}
        stationType={selectedStationDetailsTable}
        stationData={selectedStationProfile}
        onStationDataChange={handleStationProfileChange}
        onOpenAlertSettings={handleOpenStationAlertSettings}
        onOpenStationNotes={handleOpenStationNotes}
        isLoading={stationProfileLoading}
        loadError={stationProfileError}
        open={!!selectedStationForDetails}
        onClose={handleCloseStationDetails}
        hideTriggerButton
      />

      <StationNotesModal
        open={!!selectedStationForNotes}
        onClose={handleCloseStationNotes}
        stationId={selectedStationNotesId}
        stationType={selectedStationNotesTable}
        stationData={selectedStationNotesProfile}
        notes={selectedStationNotesList}
        isLoading={stationNotesLoading}
        error={stationNotesError}
        onCreateNote={createStationNote}
        onDeleteNote={deleteStationNote}
        onStationDataChange={handleStationNotesProfileChange}
        onOpenStationDetails={handleOpenStationDetails}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: isMobile ? '0' : (isSidebarCollapsed ? '72px' : '280px'),
          padding: {
            xs: (currentMenu === 'dashboard' || currentMenu === 'download') ? 0 : '12px',
            sm: (currentMenu === 'dashboard' || currentMenu === 'download') ? 0 : '16px',
            md: '30px',
          },
          minHeight: { xs: 'auto', md: '100vh' },
          backgroundColor: '#1a1b2e',
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: '16px', md: '20px' },
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        {currentMenu === 'dashboard' && (
          <DashboardV2
            realTimeDataByTable={realTimeDataByTable}
            setSelectedDevice={setSelectedDevice}
            setSelectedTable={setSelectedTable}
            stationProfiles={stationProfiles}
            stationThresholds={stationThresholds}
            alertValue={alertValue}
            alertCount={alertCount}
            onOpenStationDetails={handleOpenStationDetails}
            onOpenStationNotes={handleOpenStationNotes}
            onToggleFavorite={handleToggleDashboardFavorite}
            onNavigateToProfile={() => handleMenuClick('settings')}
            onNavigateToAlerts={() => handleMenuClick('alerts')}
            onOpenMenu={openMobileMenu}
            scrollToFavorites={scrollToFavorites}
            onScrollToFavoritesDone={() => setScrollToFavorites(false)}
            isDashboardLoading={isDashboardLoading}
            hasDashboardLoadedOnce={hasDashboardLoadedOnce}
          />
        )}
        {currentMenu === 'download' && (
          <DownloadPageV2
            realTimeDataByTable={realTimeDataByTable}
            stationProfiles={stationProfiles}
            alertCount={alertCount}
            desktopLeftOffset={isSidebarCollapsed ? 72 : 280}
            onNavigateToProfile={() => handleMenuClick('settings')}
            onNavigateToAlerts={() => handleMenuClick('alerts')}
            onOpenMenu={openMobileMenu}
            onOpenStationDetails={handleOpenStationDetails}
            onOpenStationNotes={handleOpenStationNotes}
            setSelectedDevice={setSelectedDevice}
            setSelectedTable={setSelectedTable}
          />
        )}
        {currentMenu === 'settings' && <Settings />}
        {currentMenu === 'admin' && <AdminPage />}
        {currentMenu === 'alerts' && (
          <AlertPage
            alerts={notifications}
            onClearAlerts={clearNotifications}
            onAlertClick={handleAlertClick}
            setAlertValue={setAlertValue}
            alertValue={alertValue}
            alertActivate={alertActivate}
            setAlertActivate={setAlertActivate}
            alertSent={alertSent}
            setAlertSent={setAlertSent}
            initialSelectedType={alertPagePreset.tableName}
            initialSelectedDevice={alertPagePreset.deviceId}
            initialSelectionKey={alertPagePreset.requestKey}
          />
        )}
      </Box>
    </Box>
  );
}

export default App;